import { asArray } from 'lib/array/arrayUtils'

import { MerchandiseFilterFacetForm } from './MerchandiseSearchFilters'
import { setManySearchParamValues, setSearchParamValue } from 'lib/url/searchUrlUtils'
import { objectEntries } from 'lib/object/objectUtils'
import qs from 'qs'

export const EmptyMerchandiseFilters: App.MerchandiseFilters = {
  brands: [],
  campaigns: [],
  categories: [],
  tags: [],
  options: [],
  points: undefined,
  price: undefined,
  sortBy: undefined,
}

export function mapMerchandiseFilterFormToFilters(formFilters: MerchandiseFilterFacetForm): App.MerchandiseFilters {
  const { brands, campaigns, categories, tags, points, price, sortBy, freeShipping, ...options } = formFilters

  return {
    brands: asArray(brands),
    campaigns: asArray(campaigns),
    categories: asArray(categories),
    tags: asArray(tags),
    // Only include price/points if they exist (omit if at boundary values)
    price: price ? {
      min: price.start,
      max: price.end,
    } : undefined,
    points: points ? {
      min: points.start,
      max: points.end,
    } : undefined,
    sortBy,
    freeShipping: asArray(freeShipping),
    options: Object.entries(options).map(([key, value]) => ({
      type: key,
      value: asArray(value),
    })),
  }
}

// Some keys in the merchandise filters doesn't match the query param key
const querykeyOverrides = new Map<keyof App.MerchandiseFilters, string>([
  ['name', 'search'],
])

export function getMerchandiseQueryFromFilters(
  filters: App.MerchandiseFilters,
): string {
  return objectEntries(filters).reduce<string>((nextSearch, [key, value]) => {
    if (value) {
      if (key === 'price' || key === 'points') {
        // Include price/points if either min or max is defined
        // This allows setting just one boundary if the user only adjusted one side
        const rangeValue = value as { min?: number, max?: number }
        const params: Array<{ paramName: string, paramValue: number }> = []
        if (rangeValue.min !== undefined) {
          params.push({ paramName: `${key}.min`, paramValue: rangeValue.min })
        }
        if (rangeValue.max !== undefined) {
          params.push({ paramName: `${key}.max`, paramValue: rangeValue.max })
        }
        if (params.length > 0) {
          return setManySearchParamValues(nextSearch, params)
        }
      } else if (key === 'options') {
        return setManySearchParamValues(nextSearch, (value as any).map((option: any) => ({
          paramName: `${key}.${option.type}`,
          paramValue: option.value,
        })))
      } else if (typeof value === 'string' || Array.isArray(value)) {
        return setSearchParamValue(nextSearch, querykeyOverrides.get(key) ?? key, value as any)
      }
    }
    return nextSearch
  }, '')
}

export function getMerchandiseFiltersFromQuery(queryString: string): App.MerchandiseFilters {
  const params = qs.parse(queryString, { ignoreQueryPrefix: true }) as any
  const { brands, campaigns, categories, tags, points, price, sortBy, search, freeShipping, ...options } = params

  const priceMin = params['price.min'] ? Number(params['price.min']) : undefined
  const priceMax = params['price.max'] ? Number(params['price.max']) : undefined
  const pointsMin = params['points.min'] ? Number(params['points.min']) : undefined
  const pointsMax = params['points.max'] ? Number(params['points.max']) : undefined

  return {
    brands: asArray(brands),
    campaigns: asArray(campaigns),
    categories: asArray(categories),
    tags: asArray(tags),
    // Keep reading legacy points from URL for backwards-compat, but app now uses price
    // Only create the object if at least one value is defined
    points: (pointsMin !== undefined || pointsMax !== undefined) ? {
      min: pointsMin,
      max: pointsMax,
    } : undefined,
    price: (priceMin !== undefined || priceMax !== undefined) ? {
      min: priceMin,
      max: priceMax,
    } : undefined,
    sortBy: sortBy ?? 'recommended',
    name: search,
    freeShipping: asArray(freeShipping),
    options: objectEntries<Record<string, string | Array<string>>>(options)
      .filter(([key]) => key.startsWith('options.'))
      .map(([key, value]) => ({
        type: key.replace('options.', ''),
        value: asArray(value),
      })),
  }
}

export function getMerchandiseAppliedFilterCount(
  filters: App.MerchandiseFilters,
  excludeCategoryIds: Array<string> = [],
): number {
  const categoriesCount = filters.categories?.filter(id => !excludeCategoryIds.includes(id)).length ?? 0
  const brandsCount = filters.brands?.length ?? 0
  const campaignsCount = filters.campaigns?.length ?? 0
  const tagsCount = filters.tags?.length ?? 0
  const optionsCount = filters.options?.reduce((sum, option) => sum + option.value.length, 0) ?? 0
  // Only count price OR points, not both (app uses price, points kept for backwards-compat)
  const hasPrice = !!(filters.price?.min !== undefined || filters.price?.max !== undefined)
  const hasPoints = !hasPrice && !!(filters.points?.min !== undefined || filters.points?.max !== undefined)
  const rangeCount = hasPrice || hasPoints ? 1 : 0
  const freeShippingCount = filters.freeShipping?.length ?? 0

  return categoriesCount + brandsCount + campaignsCount + tagsCount + optionsCount + rangeCount + freeShippingCount
}

/**
 * Calculates URL-based category IDs (categories from the URL path, not query params).
 * These are categories like 'kitchen-dining' that come from the URL path structure.
 *
 * @param category - The category from the URL path (if any)
 * @param urlFilters - The filters derived from URL query parameters
 * @returns Array of category IDs that come from the URL path (not query params)
 */
export function getUrlCategoryIds(
  category?: App.MerchandiseCategory,
  urlFilters?: App.MerchandiseFilters,
): Array<string> {
  // If no category or category is already in query params, return empty array
  if (!category || urlFilters?.categories?.includes(category.id)) {
    return []
  }

  // Return category and all its ancestors
  return [category.id, ...(category.ancestors?.map(a => a.id) ?? [])]
}

/**
 * Merges new filters with preserved data from existing filters.
 * Preserves the search keyword but excludes URL-path categories from query params.
 *
 * URL-path categories (like /kitchen-dining) should remain in the path, not in query params.
 * Only user-selected categories (via filter sidebar) should be in query params.
 *
 * @param newFilters - The new filters from form conversion
 * @param preserveFrom - The existing filters to preserve data from
 * @param urlCategoryIds - Array of category IDs from the URL path (to exclude from query params)
 * @returns Merged filters with preserved search keyword and only user-selected categories
 */
export function mergeFiltersWithPreservedData(
  newFilters: App.MerchandiseFilters,
  preserveFrom: App.MerchandiseFilters,
  urlCategoryIds: Array<string>,
): App.MerchandiseFilters {
  // Remove URL-path categories from the categories array
  // Only user-selected categories (not from URL path) should be in query params
  const urlCategorySet = new Set(urlCategoryIds)
  const userSelectedCategories = (newFilters.categories ?? []).filter(
    id => !urlCategorySet.has(id),
  )

  return {
    ...newFilters,
    name: preserveFrom.name,
    tags: preserveFrom.tags,
    categories: userSelectedCategories,
  }
}

/**
 * Calculates the filter count for a given set of filters, excluding URL-based categories and their ancestors.
 * This is used to show accurate filter counts in both desktop and mobile contexts.
 *
 * @param filters - The filters to count
 * @param category - The category from the URL (if any)
 * @param urlFilters - The filters derived from URL query parameters
 * @returns The number of user-applied filters (excluding URL-based categories)
 */
export function calculateMerchandiseFilterCount(
  filters: App.MerchandiseFilters,
  category?: App.MerchandiseCategory,
  urlFilters?: App.MerchandiseFilters,
): number {
  if (!category) {
    return getMerchandiseAppliedFilterCount(filters)
  }

  // If category is in urlFilters (query params), count it. Otherwise, exclude it (came from URL slug)
  const shouldExcludeCategory = !urlFilters?.categories?.includes(category.id)

  if (!shouldExcludeCategory) {
    // Category was explicitly selected by user, don't exclude anything
    return getMerchandiseAppliedFilterCount(filters)
  }

  // Exclude the URL category and all its ancestors from the count
  const ancestorIds = category.ancestors?.map(ancestor => ancestor.id) ?? []
  return getMerchandiseAppliedFilterCount(filters, [category.id, ...ancestorIds])
}
